<script setup lang="ts">
const { t, tm, rt } = useI18n()
const config = useRuntimeConfig()
const route  = useRoute()
const localePath = useLocalePath()

useReveal()

const siteUrl = config.public.siteUrl as string
const canonical = computed(() => {
  const path = route.path.endsWith('/') ? route.path : `${route.path}/`
  return `${siteUrl}${path}`
})

useHead(() => ({
  title: t('customers.meta.title'),
  meta: [
    { name: 'description',        content: t('customers.meta.description') },
    { property: 'og:title',       content: t('customers.meta.title') },
    { property: 'og:description', content: t('customers.meta.description') },
    { property: 'og:url',         content: canonical.value },
    { name: 'twitter:title',       content: t('customers.meta.title') },
    { name: 'twitter:description', content: t('customers.meta.description') },
  ],
  link: [{ rel: 'canonical', href: canonical.value }],
}))

type Benefit  = { icon: string; value: string; label: string; sub: string }
type Process  = { n: string; title: string; text: string }
type Saving   = { item: string; from: string; retail: string; you: string; save: string }
type FaqItem  = { q: string; a: string }

const benefits = computed(() =>
  (tm('customers.benefits.items') as any[]).map((it) => ({
    icon: rt(it.icon), value: rt(it.value), label: rt(it.label), sub: rt(it.sub),
  })) as Benefit[],
)
const process = computed(() =>
  (tm('how.customer') as any[]).map((it) => ({
    n: rt(it.n), title: rt(it.title), text: rt(it.text),
  })) as Process[],
)
const savings = computed(() =>
  (tm('customers.savings.items') as any[]).map((it) => ({
    item: rt(it.item), from: rt(it.from), retail: rt(it.retail), you: rt(it.you), save: rt(it.save),
  })) as Saving[],
)
const faq = computed(() =>
  (tm('customers.faq.items') as any[]).map((it) => ({
    q: rt(it.q), a: rt(it.a),
  })) as FaqItem[],
)
</script>

<template>
  <div>
    <SiteNav />

    <main>
      <PageHero
        :eyebrow="t('customers.hero.eyebrow')"
        :title="t('customers.hero.title')"
        :lead="t('customers.hero.lead')"
        :cta-primary-label="t('customers.hero.ctaPrimary')"
        cta-primary-href="#cta"
        :cta-secondary-label="t('customers.hero.ctaSecondary')"
        :cta-secondary-href="`${localePath('/')}#security`"
      />

      <!-- Benefits -->
      <section class="rod-section">
        <div class="rod-container">
          <div class="text-center max-w-3xl mx-auto mb-12" data-reveal>
            <div class="rod-eyebrow mb-5">💰 {{ t('customers.benefits.eyebrow') }}</div>
            <h2 class="rod-h2">{{ t('customers.benefits.title') }}</h2>
          </div>
          <div class="grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
            <div v-for="(e, i) in benefits" :key="i" class="rod-card flex flex-col gap-3" data-reveal>
              <div class="grid place-items-center w-12 h-12 rounded-2xl"
                   style="background: linear-gradient(140deg, rgba(245,158,11,0.18) 0%, rgba(251,146,60,0.10) 100%);
                          border: 1px solid rgba(245,158,11,0.25);
                          font-size: 22px;">
                {{ e.icon }}
              </div>
              <div class="font-display text-2xl rod-gradient-text font-bold tracking-tight">{{ e.value }}</div>
              <div class="text-sm uppercase tracking-wider text-[var(--color-text)]">{{ e.label }}</div>
              <div class="text-xs text-[var(--color-text-dim)] leading-relaxed">{{ e.sub }}</div>
            </div>
          </div>
        </div>
      </section>

      <!-- Process (reuses home keys) -->
      <section class="rod-section">
        <div class="rod-container">
          <div class="text-center max-w-3xl mx-auto mb-12" data-reveal>
            <div class="rod-eyebrow mb-5">🛠 {{ t('how.eyebrow') }}</div>
            <h2 class="rod-h2" v-html="t('how.titleB')" />
          </div>
          <div class="grid md:grid-cols-2 lg:grid-cols-3 gap-5">
            <div
              v-for="(item, i) in process"
              :key="i"
              class="rod-card flex flex-col gap-3"
              data-reveal
            >
              <div class="font-mono text-base font-bold rod-gradient-text">{{ item.n }}</div>
              <h3 class="rod-h3 leading-tight" style="font-size: 1.1rem;">{{ item.title }}</h3>
              <p class="text-[var(--color-text-muted)] leading-relaxed text-[0.92rem]">{{ item.text }}</p>
            </div>
          </div>
        </div>
      </section>

      <!-- Savings table -->
      <section class="rod-section">
        <div class="rod-container">
          <div class="text-center max-w-3xl mx-auto mb-12" data-reveal>
            <div class="rod-eyebrow mb-5">🧾 {{ t('customers.savings.eyebrow') }}</div>
            <h2 class="rod-h2">{{ t('customers.savings.title') }}</h2>
          </div>

          <div class="rod-card overflow-x-auto" data-reveal>
            <table class="w-full text-sm">
              <thead>
                <tr class="text-left text-xs uppercase tracking-wider text-[var(--color-text-dim)]">
                  <th class="py-3 px-2 font-medium">{{ t('customers.savings.cols.item') }}</th>
                  <th class="py-3 px-2 font-medium">{{ t('customers.savings.cols.hub') }}</th>
                  <th class="py-3 px-2 font-medium">{{ t('customers.savings.cols.retail') }}</th>
                  <th class="py-3 px-2 font-medium">{{ t('customers.savings.cols.you') }}</th>
                  <th class="py-3 px-2 font-medium text-right">{{ t('customers.savings.cols.save') }}</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="(s, i) in savings" :key="i" class="border-t border-white/5">
                  <td class="py-3 px-2 font-semibold">{{ s.item }}</td>
                  <td class="py-3 px-2 font-mono text-[var(--color-gold-300)]">{{ s.from }}</td>
                  <td class="py-3 px-2 text-[var(--color-text-muted)] line-through">{{ s.retail }}</td>
                  <td class="py-3 px-2 font-semibold">{{ s.you }}</td>
                  <td class="py-3 px-2 text-right font-semibold rod-gradient-text">{{ s.save }}</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </section>

      <!-- FAQ -->
      <section class="rod-section">
        <div class="rod-container">
          <div class="text-center max-w-3xl mx-auto mb-12" data-reveal>
            <div class="rod-eyebrow mb-5">❓ {{ t('customers.faq.eyebrow') }}</div>
            <h2 class="rod-h2">{{ t('customers.faq.title') }}</h2>
          </div>
          <div class="max-w-3xl mx-auto flex flex-col gap-3">
            <details v-for="(item, i) in faq" :key="i" class="rod-card group" data-reveal>
              <summary class="cursor-pointer list-none flex items-start justify-between gap-4">
                <span class="rod-h3 leading-snug" style="font-size: 1.05rem;">{{ item.q }}</span>
                <span class="text-[var(--color-gold-400)] text-2xl leading-none flex-shrink-0 transition-transform duration-200 group-open:rotate-45">+</span>
              </summary>
              <p class="mt-4 text-[var(--color-text-muted)] leading-relaxed text-[0.95rem]">{{ item.a }}</p>
            </details>
          </div>
        </div>
      </section>

      <!-- CTA -->
      <section id="cta" class="rod-section relative overflow-hidden">
        <div class="rod-aurora" />
        <div class="rod-container relative">
          <div class="rod-card rod-card--glow text-center py-16 px-6 lg:px-12 max-w-4xl mx-auto" data-reveal
               style="background: linear-gradient(135deg, rgba(245,158,11,0.12) 0%, rgba(251,146,60,0.06) 100%);
                      border-color: rgba(245,158,11,0.30);">
            <h2 class="rod-h2 mb-5 rod-gradient-text">{{ t('customers.cta.title') }}</h2>
            <p class="text-lg text-[var(--color-text-muted)] max-w-2xl mx-auto mb-10">{{ t('customers.cta.lead') }}</p>
            <div class="flex flex-wrap justify-center gap-4">
              <a :href="`${localePath('/')}#products`" class="rod-btn-primary">{{ t('customers.cta.primary') }} <span aria-hidden="true">↓</span></a>
              <a :href="`${localePath('/')}#security`" class="rod-btn-secondary">{{ t('customers.cta.secondary') }}</a>
            </div>
          </div>
        </div>
      </section>
    </main>

    <SiteFooter />
  </div>
</template>
